import React, { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SERVICE_CATALOG, serviceFormHref } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { serviceTitle } from '../i18n/serviceCopy';

const SPEED_PX_S = 42;
/** Hand drag: how many pixels the strip moves per pixel of pointer movement (higher = feels faster). */
const DRAG_SPEED_MULTIPLIER = 2.35;
const WHEEL_SPEED_MULTIPLIER = 1.85;
const DRAG_THRESHOLD_PX = 8;

const pillClass =
  'shrink-0 inline-flex items-center gap-3 rounded-full border border-[#14A098]/18 bg-white pl-1.5 pr-5 py-1.5 shadow-sm shadow-[#14A098]/6 transition-colors select-none';

const pillInteractive =
  'hover:border-[#14A098]/45 hover:bg-teal-50/80 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#14A098]/40 focus-visible:ring-offset-2';

/** Keep translate in (-segmentWidth, 0] for seamless looping with duplicate segment. */
function wrapOffset(raw, segment) {
  if (segment <= 0) return 0;
  let x = raw;
  while (x <= -segment) x += segment;
  while (x > 0) x -= segment;
  return x;
}

/**
 * Infinite horizontal marquee (right → left): no scrollbar.
 * Pauses while the user is pressing / touching (including drag). Drag horizontally to move manually.
 * Autoplay resumes when the pointer is released. Mouse wheel over the strip also nudges horizontally.
 */
export const ServiceMarquee = ({ linkable = false }) => {
  const { t } = useLanguage();
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const segmentRef = useRef(null);
  const segmentWidthRef = useRef(0);
  const offsetRef = useRef(0);
  const rafRef = useRef(0);
  const pointersRef = useRef(0);
  const draggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartOffsetRef = useRef(0);
  const dragMovedRef = useRef(false);
  const reduceMotionRef = useRef(false);

  const measureSegment = useCallback(() => {
    const el = segmentRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const w = el.getBoundingClientRect().width;
    const styles = getComputedStyle(track);
    const gapPx = parseFloat(styles.columnGap || styles.gap || '12');
    const gap = Number.isFinite(gapPx) ? gapPx : 12;
    segmentWidthRef.current = w + gap;
  }, []);

  useEffect(() => {
    reduceMotionRef.current =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    const seg = segmentRef.current;
    if (!seg) return;
    measureSegment();
    const ro = new ResizeObserver(() => measureSegment());
    ro.observe(seg);
    return () => ro.disconnect();
  }, [measureSegment, t]);

  const applyTransform = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    track.style.transform = `translate3d(${offsetRef.current}px,0,0)`;
  }, []);

  useEffect(() => {
    let prev = performance.now();
    const loop = (now) => {
      rafRef.current = requestAnimationFrame(loop);
      const dt = Math.min(64, (now - prev) / 1000);
      prev = now;
      const seg = segmentWidthRef.current;
      if (!seg || !trackRef.current) return;

      if (!reduceMotionRef.current && pointersRef.current === 0) {
        offsetRef.current -= SPEED_PX_S * dt;
        offsetRef.current = wrapOffset(offsetRef.current, seg);
      }
      trackRef.current.style.transform = `translate3d(${offsetRef.current}px,0,0)`;
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs drive pause state
  }, [t]);

  const suppressLinkNav = () => dragMovedRef.current;

  const endPointerDrag = useCallback((e) => {
    if (draggingRef.current && e?.pointerId != null && viewportRef.current?.hasPointerCapture?.(e.pointerId)) {
      try {
        viewportRef.current.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    draggingRef.current = false;
    pointersRef.current = Math.max(0, pointersRef.current - 1);
  }, []);

  const onPointerDownViewport = useCallback(
    (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      pointersRef.current += 1;
      draggingRef.current = true;
      dragStartXRef.current = e.clientX;
      dragStartOffsetRef.current = offsetRef.current;
      dragMovedRef.current = false;
      try {
        viewportRef.current?.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [],
  );

  const onPointerMoveViewport = useCallback(
    (e) => {
      if (!draggingRef.current) return;
      const dx = e.clientX - dragStartXRef.current;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX) dragMovedRef.current = true;
      const seg = segmentWidthRef.current;
      if (!seg) return;
      offsetRef.current = wrapOffset(dragStartOffsetRef.current + dx * DRAG_SPEED_MULTIPLIER, seg);
      const track = trackRef.current;
      if (track) track.style.transform = `translate3d(${offsetRef.current}px,0,0)`;
    },
    [],
  );

  const onPointerUpViewport = useCallback(
    (e) => {
      endPointerDrag(e);
    },
    [endPointerDrag],
  );

  const onPointerCancelViewport = useCallback(
    (e) => {
      endPointerDrag(e);
    },
    [endPointerDrag],
  );

  const onLostPointerCapture = useCallback(() => {
    draggingRef.current = false;
  }, []);

  const wheelHandler = useCallback((e) => {
    const seg = segmentWidthRef.current;
    if (!seg || !viewportRef.current?.contains(e.target)) return;
    let dy = e.deltaY;
    const dx = e.deltaX;
    if (Math.abs(dx) >= Math.abs(dy)) return;
    if (e.deltaMode === 1) dy *= 16;
    else if (e.deltaMode === 2) dy *= 80;
    if (Math.abs(dy) < 0.25) return;
    e.preventDefault();
    offsetRef.current = wrapOffset(offsetRef.current + dy * WHEEL_SPEED_MULTIPLIER, seg);
    applyTransform();
  }, [applyTransform]);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    vp.addEventListener('wheel', wheelHandler, { passive: false });
    return () => vp.removeEventListener('wheel', wheelHandler);
  }, [wheelHandler]);

  const renderPills = (copyKey) =>
    SERVICE_CATALOG.map((svc, idx) => {
      const Icon = svc.icon;
      const to = serviceFormHref(svc.code);
      const inner = (
        <>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#14A098] to-[#0e8f87] text-white shadow-md ring-2 ring-white/40 pointer-events-none">
            <Icon className="w-4 h-4" aria-hidden />
          </span>
          <span className="flex min-w-0 flex-col pointer-events-none">
            <span className="text-sm font-semibold tracking-tight text-[#0f2540] whitespace-nowrap">
              {serviceTitle(svc.code, t, svc.title)}
            </span>
          </span>
        </>
      );
      const key = `${copyKey}-${svc.code}-${idx}`;
      if (linkable) {
        return (
          <Link
            key={key}
            to={to}
            onClick={(ev) => {
              if (suppressLinkNav()) {
                ev.preventDefault();
                ev.stopPropagation();
              }
            }}
            className={`${pillClass} ${pillInteractive} cursor-grab active:cursor-grabbing`}
          >
            {inner}
          </Link>
        );
      }
      return (
        <div key={key} className={pillClass}>
          {inner}
        </div>
      );
    });

  return (
    <div
      ref={viewportRef}
      className="marquee-mask relative min-w-0 w-full max-w-full cursor-grab overflow-hidden rounded-2xl border border-[#14A098]/20 bg-gradient-to-r from-white/95 via-teal-50/50 to-cyan-50/40 py-3.5 shadow-[0_4px_24px_rgba(20,160,152,0.08)] active:cursor-grabbing"
      role="region"
      aria-label={t('sf.marquee_strip', 'Scroll to browse services')}
      onPointerDown={onPointerDownViewport}
      onPointerMove={onPointerMoveViewport}
      onPointerUp={onPointerUpViewport}
      onPointerCancel={onPointerCancelViewport}
      onLostPointerCapture={onLostPointerCapture}
    >
      <div
        ref={trackRef}
        className="flex w-max gap-3 px-3 sm:px-4 will-change-transform"
        style={{ transform: 'translate3d(0,0,0)' }}
      >
        <div ref={segmentRef} className="flex shrink-0 gap-3">
          {renderPills('a')}
        </div>
        <div className="flex shrink-0 gap-3" aria-hidden="true">
          {renderPills('b')}
        </div>
      </div>
    </div>
  );
};
